import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './App.css';
import { api } from './api';
import Footer from './components/Footer';

const STRATEGIES = [
  { value: 'diversified', label: 'Diversified', icon: '⚖️', desc: 'Balanced, recommended' },
  { value: 'target_return', label: 'Target Return', icon: '🎯', desc: 'Hit a specific return' },
  { value: 'top', label: 'Top Stocks', icon: '🏆', desc: 'Best performers' },
  { value: 'random', label: 'Random', icon: '🎲', desc: 'Explore possibilities' },
];

function OptimizedApp() {
  const [numStocks, setNumStocks] = useState(10);
  const [targetBeta, setTargetBeta] = useState(1.0);
  const [targetReturn, setTargetReturn] = useState('');
  const [strategy, setStrategy] = useState('diversified');
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stocks, setStocks] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [stats, setStats] = useState(null);

  const validation = useMemo(() => {
    const errors = [];
    if (strategy !== 'target_return') {
      if (numStocks < 1 || numStocks > 50) errors.push('Number of stocks must be between 1 and 50');
    }
    if (targetBeta < 0.1 || targetBeta > 3.0) errors.push('Target beta must be between 0.1 and 3.0');
    if (targetReturn && (isNaN(parseFloat(targetReturn)) || parseFloat(targetReturn) < 1 || parseFloat(targetReturn) > 50)) {
      errors.push('Target return must be between 1% and 50%');
    }
    if (strategy === 'target_return' && !targetReturn) errors.push('Target Return strategy requires a target return');
    return { isValid: errors.length === 0, errors };
  }, [numStocks, targetBeta, targetReturn, strategy]);

  useEffect(() => { loadStocks(); loadStats(); }, []);

  const loadStocks = useCallback(async () => {
    try {
      const { data } = await api.get('/stocks');
      setStocks(data.stocks);
      setSectors(data.sectors || []);
    } catch (err) { console.error('Failed to load stocks:', err); }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get('/stats');
      setStats(data);
    } catch (err) { console.error('Failed to load stats:', err); }
  }, []);

  const optimizePortfolio = useCallback(async () => {
    if (!validation.isValid) { setError(validation.errors.join(', ')); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/optimize', {
        num_stocks: parseInt(numStocks) || 10,
        target_beta: parseFloat(targetBeta) || 1.0,
        target_return: targetReturn && targetReturn.trim() ? parseFloat(targetReturn) : null,
        strategy: strategy || 'diversified'
      });
      setPortfolioData(data);
      loadStats();
      // Scroll to results smoothly
      setTimeout(() => document.getElementById('results-optimized')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Cannot connect to backend.';
      setError(msg);
    } finally { setLoading(false); }
  }, [numStocks, targetBeta, strategy, validation, loadStats, targetReturn]);

  const clearCache = useCallback(async () => {
    try { await api.post('/clear-cache'); loadStats(); }
    catch (err) { console.error('Failed to clear cache:', err); }
  }, [loadStats]);

  const resetForm = useCallback(() => {
    setNumStocks(10); setTargetBeta(1.0); setTargetReturn('');
    setStrategy('diversified'); setPortfolioData(null); setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const portfolioSummary = useMemo(() => {
    if (!portfolioData) return null;
    const totalWeight = Object.values(portfolioData.weights).reduce((s, w) => s + w, 0);
    return {
      totalWeight: totalWeight.toFixed(3),
      isBalanced: Math.abs(totalWeight - 1.0) < 0.01,
      stockCount: Object.keys(portfolioData.weights).length,
      topStock: Object.entries(portfolioData.weights).sort(([, a], [, b]) => b - a)[0]
    };
  }, [portfolioData]);

  return (
    <div id="app-optimized" className="App" role="application" aria-label="Portfolio Optimizer">
      <div id="container-optimized" className="container">

        {/* ── HEADER ── */}
        <header id="app-header-optimized" className="app-header">
          <h1 id="title-optimized">Portfolio Optimizer</h1>
          <p id="subtitle-optimized">Advanced portfolio optimization with intelligent stock selection</p>
          <div id="stats-bar" className="stats-bar" aria-live="polite">
            {stats ? (
              <>
                <span id="stats-total-stocks">📊 {stats.total_stocks} stocks available</span>
                <span id="stats-cache-size">💾 Cache: {stats.cache_size} entries</span>
              </>
            ) : (
              <span>Loading stats…</span>
            )}
            <button id="btn-clear-cache" type="button" onClick={clearCache} className="clear-cache-btn" aria-label="Clear optimization cache">Clear Cache</button>
          </div>
        </header>

        {/* ── SETTINGS FORM ── */}
        <div id="input-section-optimized" className="input-section">
          <div id="settings-card" className="settings-card">
            <h3 id="settings-heading">⚙️ Optimization Settings</h3>

            {/* Strategy Picker (pill buttons) */}
            <div id="input-group-strategy" className="input-group">
              <label>Selection Strategy</label>
              <div className="strategy-pills" role="radiogroup" aria-label="Selection strategy">
                {STRATEGIES.map(({ value, label, icon, desc }) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={strategy === value}
                    className={`strategy-pill ${strategy === value ? 'active' : ''}`}
                    onClick={() => setStrategy(value)}
                  >
                    <span className="pill-icon">{icon}</span>
                    <span className="pill-label">{label}</span>
                    <span className="pill-desc">{desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div id="input-grid" className="input-grid">

              {/* Target Return */}
              <div id="input-group-target-return" className="input-group">
                <label htmlFor="targetReturn">
                  Target Return (%)
                  {targetReturn && <span className="input-value-badge">{targetReturn}%</span>}
                </label>
                <div id="target-return-wrapper" className="target-return-wrapper">
                  <input
                    id="targetReturn"
                    type="number"
                    step="0.1"
                    value={targetReturn}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || (parseFloat(val) >= 1 && parseFloat(val) <= 50)) setTargetReturn(val);
                    }}
                    min="1" max="50"
                    placeholder="e.g., 12"
                    className={validation.isValid ? '' : 'error'}
                  />
                  <span className="target-return-suffix">%</span>
                </div>
                <small>Annual return goal: 1%–50% {strategy !== 'target_return' ? '(optional)' : '(required)'}</small>
              </div>

              {/* Beta */}
              <div id="input-group-target-beta" className="input-group">
                <label htmlFor="targetBeta">
                  Target Beta
                  <span className="input-value-badge">{targetBeta}</span>
                </label>
                <div className="slider-with-input">
                  <input
                    type="range" min="0.1" max="3.0" step="0.1"
                    value={targetBeta}
                    onChange={(e) => setTargetBeta(parseFloat(e.target.value))}
                    className="range-slider"
                    aria-label="Target beta slider"
                  />
                  <input
                    id="targetBeta"
                    type="number" step="0.1" min="0.1" max="3.0"
                    value={targetBeta}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      if (val >= 0.1 && val <= 3.0) setTargetBeta(val);
                    }}
                    className={`beta-number-input ${validation.isValid ? '' : 'error'}`}
                  />
                </div>
                <small>0.1 = conservative · 1.0 = market · 3.0 = aggressive</small>
              </div>

              {/* Number of Stocks */}
              <div id="input-group-num-stocks" className="input-group">
                <label htmlFor="numStocks">
                  Number of Stocks
                  {strategy !== 'target_return' && <span className="input-value-badge">{numStocks}</span>}
                </label>
                {strategy !== 'target_return' ? (
                  <>
                    <input
                      type="range" min="1" max="50"
                      value={numStocks}
                      onChange={(e) => setNumStocks(parseInt(e.target.value))}
                      className="range-slider"
                      aria-label="Number of stocks slider"
                    />
                    <input
                      id="numStocks"
                      type="number" min="1" max="50"
                      value={numStocks}
                      onChange={(e) => setNumStocks(parseInt(e.target.value) || 0)}
                      className={validation.isValid ? '' : 'error'}
                    />
                    <small>Choose 1–50 stocks</small>
                  </>
                ) : (
                  <div className="disabled-note">Auto-selected by optimizer for this strategy</div>
                )}
              </div>

            </div>

            {/* Validation Errors */}
            {!validation.isValid && (
              <div id="validation-errors" className="validation-errors" role="alert" aria-live="assertive">
                {validation.errors.map((err, i) => (
                  <div key={i} className="error-message">⚠️ {err}</div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div id="action-buttons-desktop" className="action-buttons">
            <button
              id="btn-optimize-desktop" type="button"
              onClick={optimizePortfolio}
              disabled={loading || !validation.isValid}
              className="optimize-btn"
              aria-busy={loading}
            >
              {loading ? (<><span className="spinner" aria-hidden="true"></span>Optimizing...</>) : '⚡ Optimize Portfolio'}
            </button>
            <button id="btn-reset-desktop" type="button" onClick={resetForm} className="reset-btn" disabled={loading}>
              🔄 Reset
            </button>
          </div>
        </div>

        {/* ── ERROR ── */}
        {error && (
          <div id="error-section-optimized" className="error-section" role="alert">
            <h3>❌ Error</h3>
            <p>{error}</p>
          </div>
        )}

        {/* ── RESULTS ── */}
        {portfolioData && (
          <section id="results-optimized" className="results results-reveal" aria-live="polite">
            <div className="results-header">
              <h2 id="results-title-optimized">📈 Portfolio Results</h2>
              <div className="optimization-info">
                <span>⏱️ {portfolioData.optimization_time}s</span>
                <span>🎯 {portfolioData.strategy_used}</span>
              </div>
            </div>

            <div id="metrics-grid" className="metrics-grid">
              {[
                { id: 'return', icon: '📈', label: 'Expected Return', value: `${(portfolioData.expected_return * 100).toFixed(2)}%`, sub: 'Annual' },
                { id: 'vol', icon: '📊', label: 'Volatility', value: `${(portfolioData.volatility * 100).toFixed(2)}%`, sub: 'Annual Risk' },
                { id: 'sharpe', icon: '⭐', label: 'Sharpe Ratio', value: portfolioData.sharpe_ratio.toFixed(3), sub: 'Risk-Adjusted' },
                { id: 'beta', icon: 'β', label: 'Portfolio Beta', value: portfolioData.actual_beta != null ? Number(portfolioData.actual_beta).toFixed(3) : 'N/A', sub: `Target: ${portfolioData.target_beta}` },
              ].map(({ id, icon, label, value, sub }) => (
                <div key={id} className="metric-card">
                  <div className="metric-icon">{icon}</div>
                  <div className="metric-label">{label}</div>
                  <div className="metric-value">{value}</div>
                  <div className="metric-subtitle">{sub}</div>
                </div>
              ))}
              {portfolioData.target_return && (
                <div className="metric-card">
                  <div className="metric-icon">🎯</div>
                  <div className="metric-label">Target Return</div>
                  <div className="metric-value">{(portfolioData.target_return * 100).toFixed(1)}%</div>
                  <div className="metric-subtitle">Achieved: {(portfolioData.expected_return * 100).toFixed(2)}%</div>
                </div>
              )}
            </div>

            <div className="time-period-disclaimer" style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', marginTop: '16px', fontSize: '0.875rem', color: '#555' }}>
              <strong>📅 Projection:</strong> $10,000 invested → approx. ${(10000 * (1 + portfolioData.expected_return)).toFixed(0)} after 1 year.
            </div>

            {portfolioSummary && (
              <div id="portfolio-summary" className="portfolio-summary">
                <h3>📊 Portfolio Summary</h3>
                <div className="summary-stats">
                  {[
                    { label: 'Total Weight', value: portfolioSummary.totalWeight, cls: portfolioSummary.isBalanced ? 'success' : 'warning' },
                    { label: 'Stocks Selected', value: portfolioSummary.stockCount },
                    { label: 'Top Holding', value: `${portfolioSummary.topStock[0]} (${(portfolioSummary.topStock[1] * 100).toFixed(1)}%)` },
                  ].map(({ label, value, cls }) => (
                    <div key={label} className="summary-item">
                      <span className="label">{label}:</span>
                      <span className={`value ${cls || ''}`}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div id="weights-section" className="weights-section">
              <h3>💼 Stock Weights &amp; Returns</h3>
              <div id="weights-grid" className="weights-grid">
                {Object.entries(portfolioData.weights)
                  .filter(([, w]) => w > 0.001)
                  .sort(([, a], [, b]) => b - a)
                  .map(([symbol, weight]) => {
                    const stock = portfolioData.stocks.find(s => s.symbol === symbol);
                    const ret = portfolioData.individual_returns?.[symbol];
                    return (
                      <div key={symbol} id={`weight-card-${symbol}`} className="weight-card">
                        <div className="weight-header">
                          <div className="symbol-info">
                            <span className="symbol">{symbol}</span>
                            <span className="company-name">{stock?.name}</span>
                          </div>
                          <div className="weight-value">{(weight * 100).toFixed(1)}%</div>
                        </div>
                        <div className="weight-bar-track">
                          <div className="weight-bar-fill" style={{ width: `${(weight * 100).toFixed(1)}%` }} />
                        </div>
                        <div className="weight-details">
                          <span className="detail-chip"><span className="chip-label">Ret:</span><span className="chip-value">{((ret ?? 0) * 100).toFixed(1)}%</span></span>
                          <span className="detail-chip"><span className="chip-label">β</span><span className="chip-value">{stock?.beta?.toFixed(2)}</span></span>
                          <span className="detail-chip"><span className="chip-label">{stock?.sector}</span></span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="success-message" style={{ marginTop: '1.5rem' }}>✅ {portfolioData.message}</div>
          </section>
        )}

        {/* ── SECTORS ── */}
        {sectors.length > 0 && (
          <div id="sectors-info" className="sectors-info">
            <h3>🏢 Available Sectors</h3>
            <div id="sectors-list" className="sectors-list">
              {sectors.map(s => <span key={s} className="sector-tag">{s}</span>)}
            </div>
          </div>
        )}

        {/* ── EDUCATIONAL CONTENT FOR ADSENSE COMPLIANCE ── */}
        <div id="educational-content" className="educational-content">
          <section className="edu-section">
            <h2>How to Use the Portfolio Optimizer</h2>
            <p>Welcome to the ultimate tool for intelligent portfolio construction. Whether you are a beginner looking to understand diversification or an experienced investor aiming to hit specific risk targets, this optimizer acts as your personal quantitative analyst.</p>
            <ol>
              <li><strong>Select a Strategy:</strong> Choose between a balanced Diversified approach, targeting a specific return, or finding top historical performers.</li>
              <li><strong>Set Your Risk Tolerance (Beta):</strong> Use the slider to define how much volatility you are comfortable with compared to the broader market.</li>
              <li><strong>Refine Inputs:</strong> Adjust the number of stocks you want to hold or set a precise annual return goal.</li>
              <li><strong>Optimize:</strong> Our algorithm instantly curates a selection of stocks and assigns optimal weightings to maximize your risk-adjusted returns (Sharpe Ratio).</li>
            </ol>
          </section>

          <section className="edu-section">
            <h2>Understanding Key Financial Metrics</h2>
            <p>To make the most of your investments, it's crucial to understand the metrics our optimizer uses to build your portfolio.</p>
            
            <div className="metric-explanation">
              <h3>What is Portfolio Beta (β)?</h3>
              <p>Beta is a measure of an investment's volatility relative to the overall market (usually the S&P 500). A beta of 1.0 means the portfolio moves perfectly with the market. A beta of 1.5 indicates it's 50% more volatile, offering higher potential returns but greater risk. A beta of 0.8 means it's 20% less volatile, providing a more conservative, defensive stance during market downturns.</p>
            </div>

            <div className="metric-explanation">
              <h3>The Sharpe Ratio Decoded</h3>
              <p>Named after Nobel laureate William F. Sharpe, this ratio helps investors understand the return of an investment compared to its risk. It shows how much excess return you receive for the extra volatility you endure holding a riskier asset. A higher Sharpe Ratio is always better, as it indicates excellent risk-adjusted performance rather than just raw speculative gains.</p>
            </div>

            <div className="metric-explanation">
              <h3>Why Diversification Matters</h3>
              <p>Diversification is often called the only "free lunch" in investing. By spreading your capital across different sectors (like Technology, Healthcare, and Financials) and different individual companies, you reduce the impact of any single stock's poor performance on your overall portfolio. Our algorithm inherently seeks diversified assets to lower your total risk profile.</p>
            </div>
          </section>

          <section className="edu-section faq-section">
            <h2>Frequently Asked Questions</h2>
            
            <details className="faq-item">
              <summary>Is this application providing financial advice?</summary>
              <div className="faq-answer">No. The Portfolio Optimizer is an educational tool designed to demonstrate quantitative finance concepts. It uses historical data to calculate optimal weightings mathematically. Past performance does not guarantee future results, and you should always consult with a licensed financial advisor before making real-world investment decisions.</div>
            </details>
            
            <details className="faq-item">
              <summary>How accurate are the expected returns?</summary>
              <div className="faq-answer">The expected returns and volatility figures are projections based on historical asset behavior. While our algorithm uses robust modern portfolio theory models, financial markets are unpredictable. These figures represent statistical probabilities, not certainties or guarantees of future performance.</div>
            </details>

            <details className="faq-item">
              <summary>Why didn't the optimizer give me the exact number of stocks I requested?</summary>
              <div className="faq-answer">Our mathematical models prioritize finding the mathematically optimal Sharpe Ratio for your given risk constraints. In some cases, the algorithm will determine that a smaller selection of highly optimized assets provides a better risk-to-reward ratio than forcing capital into suboptimal stocks just to meet a numerical quota.</div>
            </details>
          </section>
        </div>

      </div>

      {/* Mobile Sticky Bar */}
      <div id="mobile-action-bar" className="mobile-action-bar">
        <div className="action-buttons">
          <button id="btn-optimize-mobile" type="button" onClick={optimizePortfolio} disabled={loading || !validation.isValid} className="optimize-btn">
            {loading ? (<><span className="spinner"></span>Optimizing...</>) : '⚡ Optimize'}
          </button>
          <button id="btn-reset-mobile" type="button" onClick={resetForm} className="reset-btn" disabled={loading}>Reset</button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default OptimizedApp;
